import {
  Autocomplete,
  AutocompleteCloseReason,
  AutocompleteProps,
  Button,
  Divider,
  FormControl,
  FormControlProps,
  InputProps,
  Paper,
  type SxProps,
  TextField,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { AutoCompleteOption } from 'src/components/Forms/types';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import { Tooltip, TooltipProps } from 'src/components/Tooltip/Tooltip';
import { toSxArray } from 'src/utils/sx';

import { getFormAutocompleteMobileInputZoomSx } from './form-autocomplete-mobile-input-sx';

export type DefaultOption = AutoCompleteOption | string;
export type AutocompleteValue<T extends DefaultOption = DefaultOption> = T;

type PopperSlotProps = { modifiers?: unknown[]; style?: React.CSSProperties };

type ListboxSlotProps = { style?: React.CSSProperties };

export type FormAutocompleteProps<T extends DefaultOption = DefaultOption> = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'options' | 'name'
> & {
  options: AutocompleteValue<T>[];
  required?: boolean | string;
  label?: string;
  loading?: boolean;
  openOnFocus?: boolean;
  blurOnSelect?: boolean; // Blur input after selection. Defaults to true.
  blurOnClose?: boolean; // Blur input on close via escape/blur. Defaults to true.
  // TODO: expand value type to support multiple options
  value?: AutocompleteValue<T> | null;
  multiple?: boolean;
  autocompleteProps?: Partial<AutocompleteProps<AutocompleteValue<T>, boolean, true, boolean>>;
  textFieldProps?: TextFieldProps;
  inputProps?: InputProps;
  creatable?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  tooltipText?: string | React.ReactNode;
  onAddNewItem?: () => void;
  onAddNewItemLabel?: string;
  textfieldRef?: React.RefObject<HTMLInputElement>;
  underlined?: boolean;
  disabled?: boolean;
} & CommonFormFieldProps<'autocomplete', AutocompleteValue<T> | null | undefined>;

export const FormAutocomplete = <T extends DefaultOption = DefaultOption>(
  props: FormAutocompleteProps<T>,
) => {
  const {
    name,
    label,
    required,
    options,
    autocompleteProps,
    textFieldProps,
    inputProps,
    children,
    dependsOn = [],
    showsIf = [],
    defaultValue,
    loading = false,
    multiple = false,
    creatable = false,
    openOnFocus = false,
    blurOnSelect = true,
    blurOnClose = true,
    tooltipProps,
    tooltipText,
    onAddNewItem,
    onAddNewItemLabel,
    textfieldRef,
    underlined = false,
    disabled = false,
    ...rest
  } = props;

  const { sx: autocompleteSx, ...restAutocompleteProps } = autocompleteProps ?? {};

  const localRef = useRef<HTMLInputElement | null>(null);
  const inputRef = textfieldRef || localRef;
  const lastCloseReasonRef = useRef<AutocompleteCloseReason | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // On desktop, pin the popup inside the viewport (extra right/bottom padding) so a tall
  // list near a scrollable panel edge can't push the page into a flip/scrollbar flicker loop.
  const slotSources = restAutocompleteProps as unknown as {
    slotProps?: { popper?: PopperSlotProps; listbox?: ListboxSlotProps } & Record<string, unknown>;
    componentsProps?: { popper?: PopperSlotProps };
    ListboxProps?: ListboxSlotProps;
  };
  const incomingPopperProps = slotSources.slotProps?.popper ?? slotSources.componentsProps?.popper;
  const incomingListboxProps = slotSources.slotProps?.listbox ?? slotSources.ListboxProps;

  const mergedSlotProps = isMobile
    ? slotSources.slotProps
    : {
        ...slotSources.slotProps,
        popper: {
          ...incomingPopperProps,
          modifiers: [
            { name: 'offset', options: { offset: [0, 4] } },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport' as const,
                altAxis: true,
                padding: { top: 8, left: 8, right: 42, bottom: 42 },
              },
            },
            ...(incomingPopperProps?.modifiers ?? []),
          ],
        },
        listbox: {
          ...incomingListboxProps,
          style: { maxHeight: 'min(40vh, 320px)', ...incomingListboxProps?.style },
        },
      };
  const { control, formState, resetField } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  // If the field depends on another field, reset value when the field it depends on changes.
  const values = useWatch({ control, name: dependsOn });

  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if (values?.length > 0) {
      resetField(name);
    }
  }, [values, resetField, name]);

  useEffect(() => {
    const closeReason = lastCloseReasonRef.current;
    const shouldBlurOnClose = closeReason === 'escape' || closeReason === 'blur';

    if (!isOpened && blurOnClose && shouldBlurOnClose && inputRef.current) {
      inputRef.current.blur();
    }
  }, [blurOnClose, inputRef, isOpened]);

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined;
    if (textFieldProps?.autoFocus) {
      timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [textFieldProps?.autoFocus, inputRef]);

  // Having a string type in autocompletes will allow us to use an
  // entity directly in a form, instead of having to transform their
  // values into objects
  const getValue = useCallback(
    <T extends DefaultOption>({ value }: { value?: AutocompleteValue<T> | string }) => {
      const currentValue = typeof value === 'string' ? value : value?.id || value?.value;
      const selectedOption = options.find(
        (option) => (typeof option === 'string' ? option : option.id) === currentValue,
      );

      return selectedOption ?? value;
    },
    [options],
  );

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return;
  }

  return (
    <FormControl
      variant={textFieldProps?.variant || 'standard'}
      {...rest}
      sx={{ width: '100%', ...rest.sx }}
      error={!!errorMsg}
    >
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field }) => (
          <Autocomplete
            disabled={disabled}
            multiple={multiple}
            aria-label={label}
            options={options}
            openOnFocus={openOnFocus}
            PaperComponent={
              creatable
                ? ({ children }: HTMLAttributes<HTMLElement>) => {
                    return (
                      <Paper sx={autocompleteProps?.componentsProps?.paper?.sx || {}}>
                        {children}
                        <Button
                          color="primary"
                          fullWidth
                          sx={{
                            justifyContent: 'flex-start',
                            padding: 2,
                            borderTop: '1px solid',
                            borderRadius: 0,
                            borderColor: theme.palette.custom.OV.sys.outlineVariant,
                          }}
                          onMouseDown={onAddNewItem}
                        >
                          {onAddNewItemLabel}
                        </Button>
                      </Paper>
                    );
                  }
                : undefined
            }
            value={getValue<T>(field)}
            loading={loading}
            renderOption={(props, option) => {
              const finalLabel = typeof option === 'string' ? option : option.label;
              const isEmptyOption =
                (typeof option !== 'string' && option?.id === '') || option === '';
              return (
                <li {...props} key={typeof option === 'string' ? option : option.id}>
                  <Typography noWrap>
                    {isEmptyOption ? (
                      <em style={{ fontSize: '0.875rem' }}>{finalLabel || 'Empty'}</em>
                    ) : (
                      finalLabel
                    )}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <>
                <Tooltip title={isOpened ? '' : tooltipText} {...tooltipProps}>
                  <TextField
                    error={!!errorMsg}
                    variant={textFieldProps?.variant || 'standard'}
                    label={label}
                    {...params}
                    {...textFieldProps}
                    sx={
                      [
                        ...toSxArray(textFieldProps?.sx),
                        getFormAutocompleteMobileInputZoomSx(theme),
                      ] as SxProps<Theme>
                    }
                    inputRef={inputRef}
                    InputProps={{
                      // MUI doesn't support `disableUnderline` for `outlined` input variant ¯\_(ツ)_/¯
                      ...(!textFieldProps?.variant || textFieldProps?.variant !== 'outlined'
                        ? { disableUnderline: true }
                        : {}),
                      ...params.InputProps,
                      ...inputProps,
                    }}
                  />
                </Tooltip>
                {!!underlined && !rest.hidden && (
                  <Divider sx={{ alignSelf: 'stretch', marginTop: '0.3rem' }} />
                )}
              </>
            )}
            isOptionEqualToValue={(option, value) => {
              if (typeof option !== 'string' && typeof value !== 'string') {
                return option?.id === value?.id && option?.label === value?.label;
              }
              return option === value;
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option?.label ?? '';
            }}
            sx={
              [
                ...toSxArray(autocompleteSx),
                getFormAutocompleteMobileInputZoomSx(theme),
              ] as SxProps<Theme>
            }
            {...restAutocompleteProps}
            slotProps={mergedSlotProps as typeof restAutocompleteProps.slotProps}
            blurOnSelect={blurOnSelect}
            onChange={(e, value, reason) => {
              if (blurOnSelect) {
                inputRef.current?.blur();
              }
              field.onChange(value);
              autocompleteProps?.onChange?.(e, value, reason);
            }}
            onOpen={(e) => {
              autocompleteProps?.onOpen?.(e);
              setIsOpened(true);
            }}
            onClose={(e, reason) => {
              lastCloseReasonRef.current = reason;
              setIsOpened(false);
              autocompleteProps?.onClose?.(e, reason);
            }}
          />
        )}
      />
    </FormControl>
  );
};
